/**
 * PaymentRenderers.js
 *
 * All rendering methods for the payment modal UI.
 * Handles HTML generation for payment methods, order items, and payment flows.
 */

export class PaymentRenderers {
  constructor(t, state) {
    this.t = t // Translation function
    this.state = state // PaymentState instance
  }

  /**
   * Render main modal structure
   */
  renderModal(renderOrderItems, renderPaymentOptions) {
    return `
      <div id="unified-payment-modal" class="unified-payment-overlay">
        <div class="payment-modal">
          <button class="modal-close" id="unified-payment-close">
            <i class="fas fa-times"></i>
          </button>

          <div class="order-summary">
            <h3 class="summary-title">${this.t('order.summaryTitle', 'Order Summary')}</h3>
            ${renderOrderItems}
          </div>

          <div class="payment-options">
            <div id="payment-content">
              ${renderPaymentOptions}
            </div>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Render order items list
   */
  renderOrderItems(sourceItems, beforeOrder, getItemImage, getItemModifiers, formatCurrency, getItemTotalPrice) {
    if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
      return `<div class="order-summary-error">${this.t('payment.noOrderData', 'No order data available')}</div>`
    }

    const orderItemsHtml = sourceItems.map((item, index) => `
      <div class="order-item" data-index="${index}" ${item.cartId ? `data-cart-id="${item.cartId}"` : ''}>
        ${getItemImage(item) ? `
          <div class="item-image" style="width:56px; height:56px; flex:0 0 56px; border-radius:8px; overflow:hidden;">
            <img src="${getItemImage(item)}" alt="${item.name}" style="display:block; width:100%; height:100%; object-fit:cover; border-radius:8px;">
          </div>
        ` : ''}
        <div class="item-quantity${beforeOrder ? '' : ' read-only'}">${this.state.getItemQuantity(item)}x</div>
        <div class="item-details">
          <div class="item-name">${item.name}</div>
          <div class="item-options">${getItemModifiers(item)}</div>
        </div>
        <div class="item-price">${formatCurrency(getItemTotalPrice(item))}</div>
        ${beforeOrder ? `
          <div class="item-actions" ${item.cartId ? `data-cart-id="${item.cartId}"` : ''}>
            <button class="qty-btn decrease-qty" title="Decrease" aria-label="Decrease quantity">
              <i class="fas fa-minus"></i>
            </button>
            <span class="qty-value">${item.quantity || 1}</span>
            <button class="qty-btn increase-qty" title="Increase" aria-label="Increase quantity">
              <i class="fas fa-plus"></i>
            </button>
            <button class="remove-item" title="Remove" aria-label="Remove item">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        ` : ''}
      </div>
    `).join('')

    return `
      ${!beforeOrder ? `
        <div class="after-order-hint">
          <i class="fas fa-lock"></i>
          <span>${this.t('payment.orderLocked', 'Order placed — items are locked.')}</span>
        </div>
      ` : ''}
      <div class="order-items">
        ${orderItemsHtml}
      </div>
      <div class="order-calculation">
        <div class="total-row">
          <div class="total-label">${this.t('order.totalLabel', 'Total')}</div>
          <div class="total-amount">${formatCurrency(this.state.getOrderTotal(sourceItems))}</div>
        </div>
      </div>
      <span class="order-summary-divider"></span>
    `
  }

  /**
   * Render payment options (method selection or specific method view)
   */
  renderPaymentOptions(currentPaymentMethod) {
    if (currentPaymentMethod) {
      return this.renderPaymentMethodView(currentPaymentMethod)
    }

    return `
      <div class="payment-header">
        <h2 class="payment-title">${this.t('payment.selectMethod', 'Select Payment Method')}</h2>
        <p class="payment-subtitle">${this.t('payment.finishInstruction', 'You have to finish payment to complete the order. You can receive a receipt at the counter or get it electronically.')}</p>
      </div>
      ${this.renderPaymentMethods()}
      ${this.renderAdditionalOptions()}
    `
  }

  /**
   * Render payment method selection buttons
   */
  renderPaymentMethods() {
    return `
      <div class="payment-methods">
        <div class="payment-method qr-method" data-method="QR_CODE">
          <span class="method-indicator"></span>
          <div class="method-header">
            <div class="method-icon">
              <i class="fas fa-qrcode"></i>
            </div>
            <div>
              <div class="method-title">${this.t('payment.qrMethod', 'QR Code Payment')}</div>
            </div>
          </div>
        </div>

        <div class="payment-method blik-method" data-method="BLIK">
          <span class="method-indicator"></span>
          <div class="method-header">
            <div class="method-icon">
              <i class="fas fa-key"></i>
            </div>
            <div>
              <div class="method-title">${this.t('payment.blikMethod', 'BLIK Code Payment')}</div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Render additional payment options (staff/counter)
   */
  renderAdditionalOptions() {
    return `
      <div class="additional-options-title">${this.t('payment.alternativeOptions', 'Alternative Options')}</div>
      <div class="additional-options">
        <div class="option-card" data-method="STAFF_PAYMENT">
          <div class="option-icon">
            <i class="fas fa-user-tie"></i>
          </div>
          <div class="option-title">${this.t('payment.askStaff', 'Ask Staff for Help')}</div>
          <div class="option-description">${this.t('payment.askStaffHint', 'A staff member will assist you')}</div>
        </div>

        <div class="option-card" data-method="DESK_PAYMENT">
          <div class="option-icon">
            <i class="fas fa-cash-register"></i>
          </div>
          <div class="option-title">${this.t('payment.payAtCounter', 'Pay at Counter')}</div>
          <div class="option-description">${this.t('payment.payAtCounterHint', 'Cash or card accepted at counter')}</div>
        </div>
      </div>
    `
  }

  /**
   * Render specific payment method view
   */
  renderPaymentMethodView(method) {
    switch (method) {
      case 'qr_code':
        return this.renderQRPaymentView()
      case 'blik':
        return this.renderBLIKPaymentView()
      case 'STAFF_PAYMENT':
      case 'DESK_PAYMENT':
      case 'staff_payment':
      case 'desk_payment':
        return this.renderStaffDeskFallback(method)
      default:
        return this.renderPaymentOptions(null)
    }
  }

  /**
   * Render QR code payment view
   */
  renderQRPaymentView() {
    return `
      <div class="qr-payment-view">
        <div class="payment-method-header">
          <button class="back-btn" id="payment-back-btn">
            <i class="fas fa-arrow-left"></i>
          </button>
          <h3>${this.t('payment.qrMethod', 'QR Code Payment')}</h3>
        </div>

        <div class="qr-code-display">
          <div id="qr-code-container" class="qr-code-placeholder"></div>
        </div>

        <div class="qr-instructions">
          <h4>${this.t('payment.scanQr', 'Scan QR code with your phone to pay')}</h4>
          <p>${this.t('payment.openBankApp', 'Open your banking or payment app and scan the code above')}</p>
        </div>

        <div class="payment-actions">
          <button class="btn btn-outline" id="cancel-qr-payment">
            ${this.t('payment.cancelPayment', 'Cancel Payment')}
          </button>
        </div>
      </div>
    `
  }

  /**
   * Render BLIK payment view
   */
  renderBLIKPaymentView() {
    return `
      <div class="blik-payment-view">
        <div class="payment-method-header">
          <button class="back-btn" id="payment-back-btn">
            <i class="fas fa-arrow-left"></i>
          </button>
          <h3>${this.t('payment.blikMethod', 'BLIK Payment')}</h3>
        </div>

        <div class="blik-instructions">
          <h4>${this.t('payment.blikEnterCode', 'Enter your 6-digit BLIK code')}</h4>
          <p>${this.t('payment.blikGenerate', 'Generate a BLIK code in your banking app')}</p>
        </div>

        <div class="blik-code-input">
          <div class="code-display" id="blik-code-display">
            <span class="code-digit">-</span>
            <span class="code-digit">-</span>
            <span class="code-digit">-</span>
            <span class="code-digit">-</span>
            <span class="code-digit">-</span>
            <span class="code-digit">-</span>
          </div>
        </div>

        <div class="blik-contact-optional" style="margin: 12px 0 0;">
          <div style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 6px;">${this.t('payment.optionalContact', 'Optional: add contact to receive a digital receipt')}</div>
          <div style="display:flex; gap:8px;">
            <input id="blik-contact-email" type="email" placeholder="${this.t('payment.contactEmailPlaceholder', 'Email')}"
                   style="flex:1; padding:10px 12px; border:1px solid var(--color-border-light); border-radius:10px;" />
            <input id="blik-contact-phone" type="tel" placeholder="${this.t('payment.contactPhonePlaceholder', 'Phone')}"
                   style="flex:1; padding:10px 12px; border:1px solid var(--color-border-light); border-radius:10px;" />
          </div>
          <label style="display:flex; align-items:center; gap:8px; margin-top:8px; font-size: 13px; color: var(--color-text-secondary);">
            <input id="blik-digital-receipt-consent" type="checkbox" />
            ${this.t('payment.digitalReceiptConsent', 'I agree to receive a digital receipt at the provided contact')}
          </label>
        </div>

        <div class="blik-keypad">
          ${this.renderBLIKKeypad()}
        </div>

        <div class="payment-actions">
          <button class="btn btn-primary" id="submit-blik-code" disabled>
            <i class="fas fa-check"></i>
            ${this.t('payment.submitPayment', 'Submit Payment')}
          </button>
          <button class="btn btn-outline" id="cancel-blik-payment">
            ${this.t('payment.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    `
  }

  /**
   * Render BLIK keypad
   */
  renderBLIKKeypad() {
    return `
      <div class="keypad-grid">
        ${[1,2,3,4,5,6,7,8,9,'C',0,'⌫'].map(key => `
          <button class="keypad-btn" data-key="${key}">${key}</button>
        `).join('')}
      </div>
    `
  }

  /**
   * Render staff/desk payment fallback view
   */
  renderStaffDeskFallback(method) {
    const isStaff = method === 'STAFF_PAYMENT' || method === 'staff_payment'

    return `
      <div class="staff-desk-fallback">
        <div class="payment-method-header">
          <button class="back-btn" id="payment-back-btn">
            <i class="fas fa-arrow-left"></i>
          </button>
          <h3>${isStaff ? this.t('payment.staffPaymentTitle', 'Staff Payment') : this.t('payment.counterPaymentTitle', 'Pay at Counter')}</h3>
        </div>

        <div class="fallback-content">
          <div class="fallback-icon">
            <i class="fas ${isStaff ? 'fa-user-tie fa-4x' : 'fa-credit-card fa-4x'}"></i>
          </div>

          <div class="fallback-instructions">
            <h4>${isStaff ? this.t('payment.staffPaymentSubtitle', 'Staff will assist with payment') : this.t('payment.counterPaymentSubtitle', 'Pay when collecting your order')}</h4>
            <p>${isStaff ?
              this.t('payment.staffPaymentDescription', 'A staff member has been notified and will come to assist you with payment options.') :
              this.t('payment.counterPaymentDescription', 'Please pay at the counter when collecting your order.')
            }</p>
          </div>

          <div class="fallback-status">
            <div class="status-indicator ${isStaff ? 'staff-notified' : 'order-ready'}">
              <i class="fas ${isStaff ? 'fa-bell' : 'fa-check-circle'}"></i>
              <span>${isStaff ? this.t('payment.staffNotified', 'Staff Notified') : this.t('payment.orderConfirmed', 'Order Confirmed')}</span>
            </div>
          </div>
        </div>

        <div class="payment-actions">
          <button class="btn btn-primary" id="confirm-fallback-payment">
            <i class="fas fa-check"></i>
            ${isStaff ? this.t('payment.waitingForStaff', 'Waiting for Staff') : this.t('cart.confirm_order', 'Confirm')}
          </button>
        </div>
      </div>
    `
  }
}
